import { useState } from 'react'
import type { FormEvent } from 'react'
import { useT } from '../lang'
import { ApiError, api, type Me } from '../api'
import { Field } from '../components/bits'
import { Link } from '../router'

/** One field pair, one button, and a way out.
 *
 *  The link used to be absent, and the comment here used to explain why: there
 *  was no mail path in this system, so it would have gone to a page that could
 *  not help, and an administrator reset passwords with `scripts/mkuser.sh
 *  --passwd` over ssh. That stopped being true when mail arrived, and a comment
 *  explaining a limitation that has been lifted is worse than no comment.
 *
 *  `mkuser.sh --passwd` still works and is still the break-glass path, so a
 *  mail outage cannot lock staff out of their own receiver.
 */
export function Login({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const t = useT()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post('/v1/auth/login', { email, password })
      // Read identity back from the server rather than trusting the login
      // response, so role and scope always come from one place.
      onSignedIn(await api.get<Me>('/v1/me'))
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.code === 'invalid_credentials'
            ? 'That email and password do not match an account.'
            : err.code === 'too_many_attempts'
              ? 'Too many attempts. Try again in a few minutes.'
              : err.detail ?? err.code
          : 'Could not reach the receiver.',
      )
      setBusy(false)
    }
  }

  return (
    <div className="login-wrap">
      <div className="login">
        <div className="wordmark">
          <span className="dot" aria-hidden="true" />
          Phoniq
        </div>
        {/* The one line the product puts on a wall, and the only place it
            appears. "Listen. Understand." was two verbs and a promise nobody
            can check. This is a claim about the sea that happens to be the
            whole reason to buy a hydrophone: sound arrives before sight does.
            A failing pump, a boat, a predator, a change in how the fish move --
            every one of them is audible first.

            Kept rare on purpose. A line like this earns its force by not being
            furniture, so it is here and nowhere else in the application. */}
        <p className="tag">{t('You hear it before you see it.')}</p>

        <form className="card stack" onSubmit={submit} style={{ gap: 18 }}>
          <Field label="Email">
            <input
              type="email" value={email} autoComplete="username" required autoFocus
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="Password">
            <input
              type="password" value={password} autoComplete="current-password" required
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          {error && <div className="notice bad">{error}</div>}

          <button className="btn primary" type="submit" disabled={busy || !email || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13 }}>
          <Link to="/auth/forgot">{t('Forgotten your password?')}</Link>
        </p>

        {/* Still true, and still worth saying: an account here is something
            somebody granted, not something you can help yourself to. What has
            changed is that the granting arrives by mail rather than over ssh. */}
        <p className="faint" style={{ textAlign: 'center', marginTop: 12, fontSize: 12 }}>
          {t('Accounts are created by invitation. There is no sign-up.')}
        </p>
      </div>
    </div>
  )
}
